// src/pages/BookAppointment.jsx
import { useState } from 'react';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import SummaryApi from '../common/index'
import { Helmet } from "react-helmet";

const BookAppointment = () => {
  const [loading, setLoading] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    email: '',
    treatment: '',
    area: '',
    branch: '',
    select_date: '',
    description: ''
  });

  // Get today's date in YYYY-MM-DD format to disable previous dates
  const today = new Date().toISOString().split('T')[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile_number') {
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleanedValue }));
      if (errors.mobile_number) setErrors(prev => ({ ...prev, mobile_number: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    let newErrors = {};
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
    if (!formData.mobile_number) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobile_number)) {
      newErrors.mobile_number = "Enter a valid 10-digit number starting with 6-9";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const formBody = new URLSearchParams();
      Object.keys(formData).forEach(key => {
        formBody.append(key, formData[key]);
      });

      const response = await fetch(SummaryApi.SendMail.url, {
        method: SummaryApi.SendMail.method,
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody,
      });

      const result = await response.json();

      if (result.status === "success") {
        setLoading(false);
        setIsConfirmed(true);

        setTimeout(() => {
          alert("Success! Your appointment request has been sent.");
          setFormData({
            full_name: '', mobile_number: '', email: '', treatment: '',
            area: '', branch: '', select_date: '', description: ''
          });
          window.location.reload();
        }, 800);
        
      } else {
        setLoading(false);
        alert(result.message || "Failed to send. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setLoading(false);
      alert("An error occurred. Please check your connection.");
    }
  };

  return (
    <main className="pb-6 bg-gray-50 min-h-screen">
      <Helmet>
  <title>Book Dental Appointment in Hyderabad | Dr. Sridhar Dental Clinic</title>
  <meta 
    name="description" 
    content="Book your dental appointment in Hyderabad at Dr. Sridhar Dental Clinic. Book online for implants, root canal, Invisalign, and complete dental care." 
  />
  <meta 
    name="keywords" 
    content="book dental appointment Hyderabad, dentist appointment online, dental clinic booking Hyderabad, Dr Sridhar dental appointment" 
  />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link 
    rel="canonical" 
    href="https://sridhardental.com/book-appointment" />
</Helmet>
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">Book Your Appointment</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">World-class dental care is just a click away.</p>
        </div>
      </section>

      <section className="py-16 -mt-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-[2rem] shadow-2xl p-8 md:p-14 mt-8 border border-gray-100">
            <SectionTitle title="Appointment Details" />
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Full Name *</label>
                  <input name="full_name" value={formData.full_name} onChange={handleChange} type="text" placeholder="Your Full Name" required className={`w-full p-4 bg-gray-50 border rounded-xl outline-none transition-all ${errors.full_name ? 'border-red-500' : 'border-gray-200 focus:ring-2 focus:ring-blue-500'}`} />
                  {errors.full_name && <p className="text-red-500 text-xs ml-1">{errors.full_name}</p>}
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Phone Number *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-4 text-gray-400 font-medium">+91</span>
                    <input name="mobile_number" value={formData.mobile_number} onChange={handleChange} type="tel" placeholder="10-digit Number" required className={`w-full pl-14 pr-4 py-4 bg-gray-50 border rounded-xl outline-none transition-all ${errors.mobile_number ? 'border-red-500' : 'border-gray-200 focus:ring-2 focus:ring-blue-500'}`} />
                  </div>
                  {errors.mobile_number && <p className="text-red-500 text-xs ml-1">{errors.mobile_number}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Email Address</label>
                  <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="example@gmail.com" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">City / Area *</label>
                  <input name="area" value={formData.area} onChange={handleChange} type="text" placeholder="e.g. Vijayawada" required className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Select Treatment *</label>
                  <select name="treatment" value={formData.treatment} onChange={handleChange} required className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer">
                    <option value="">Choose Treatment</option>
                    <option>Smile Designing & Cosmetic Dentistry</option>
                    <option>Dental Implants</option>
                    <option>Periodontal Treatment</option>
                    <option>Laser Dentistry</option>
                    <option>Root Canal (Endodontics)</option>
                    <option>Oral & Maxillofacial Surgery</option>
                    <option>Pediatric Dentistry</option>
                    <option>Crowns, Bridges & Dentures</option>
                    <option>Orthodontics</option>
                    <option>Advanced Denture Solutions</option>
                    <option>Dental Radiology & Diagnostics</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Preferred Branch *</label>
                  <select name="branch" value={formData.branch} onChange={handleChange} required className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer">
                    <option value="">Select Location</option>
                    <option value="VIJAYAWADA">VIJAYAWADA</option>
                    <option value="KPHB HYDERABAD">KPHB HYDERABAD</option>
                    <option value="AMEERPET">AMEERPET</option>
                    <option value="NALLAGANDLA(BHEL)">NALLAGANDLA(BHEL)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700 ml-1">Appointment Date *</label>
                <input 
                  name="select_date" 
                  value={formData.select_date} 
                  onChange={handleChange} 
                  type="date" 
                  min={today} // This line blocks previous dates
                  required 
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700 ml-1">Additional Notes</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none" placeholder="How can we help you?" />
              </div>

              <Button 
                type="submit" 
                disabled={loading || isConfirmed} 
                className={`w-full py-5 text-xl font-bold rounded-xl shadow-lg transition-all flex justify-center items-center gap-3 ${isConfirmed ? 'bg-green-600 hover:bg-green-600' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : isConfirmed ? (
                    "Appointment Confirmed!"
                ) : (
                    "Confirm Appointment"
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BookAppointment;